// The Task List's column order, as saved in the browser. Only the data columns can be dragged into another order; the
// select box, the complete ring and the title always lead and ⋯ always ends the row. Orders saved by older builds also
// hold 'duplicate', 'taskId', 'title', 'complete' and 'delete' (today's 'actions'), with the complete tick and trash at
// the far right, so only the movable columns are read back from them. 'timeEstimate' was the Estimated column, dropped
// (Petr, 2026-10-23).
const LEADING_COLUMNS = ['taskId', 'complete', 'title'];
const TRAILING_COLUMNS = ['actions'];
const RETIRED_COLUMNS = ['duplicate', 'delete', 'details', 'timeEstimate'];
const FIXED_COLUMNS = [...LEADING_COLUMNS, ...TRAILING_COLUMNS, ...RETIRED_COLUMNS];

/** The data columns in the student's order, from any saved form (a non-list reads as no order). */
export const movableWorkloadColumns = (saved: unknown): string[] =>
  Array.isArray(saved) ? saved.filter(column => typeof column === 'string' && !FIXED_COLUMNS.includes(column)) : [];

/**
 * The full order for the table: fixed columns in their places around the student's order. With nothing reordered it is
 * empty, which leaves the columns in the order they are defined.
 */
export const workloadColumnsOrder = (saved: unknown, knownColumns: string[] = []): string[] => {
  const movable = movableWorkloadColumns(saved);
  if (movable.length === 0) return [];
  // Columns added since the order was saved (Estimated, Planned, Used) join the end of the data columns, before ⋯.
  const added = movableWorkloadColumns(knownColumns).filter(column => !movable.includes(column));
  return [...LEADING_COLUMNS, ...movable, ...added, ...TRAILING_COLUMNS];
};

/**
 * Columns hidden by default: those that only repeat what the view already says -
 * the course when one course, activity or category is picked, and the reading source and pages when nothing listed is a
 * reading. A student who switches one back on keeps it ("shown anyway").
 *
 * Never the category, though it repeats the group's name when grouped by category: it's where a task gets one, and
 * hidden there, the tasks under "No category" (or a picked "Uncategorized") had no way to get one (Petr, 2026-10-19).
 */
export const autoHiddenWorkloadColumns = ({
  oneCommitment,
  hasReadings
}: {
  oneCommitment: boolean;
  hasReadings: boolean;
}): string[] => [...(oneCommitment ? ['courseName'] : []), ...(hasReadings ? [] : ['reading', 'pageRanges'])];

/** The columns to hide: those the student hid, and the auto-hidden ones they haven't switched back on. */
export const hiddenWorkloadColumns = (userHidden: string[], autoHidden: string[], shownAnyway: string[]): string[] =>
  Array.from(new Set([...userHidden, ...autoHidden.filter(column => !shownAnyway.includes(column))]));

/**
 * Switching a column on or off in the Columns menu. An auto-hidden column is switched on by adding it to "shown anyway"
 * (and off by taking it out again); any other column by the student's own hidden list, as before.
 */
export const toggleWorkloadColumn = (
  column: string,
  { userHidden, autoHidden, shownAnyway }: { userHidden: string[]; autoHidden: string[]; shownAnyway: string[] }
): { userHidden: string[]; shownAnyway: string[] } => {
  const hidden = hiddenWorkloadColumns(userHidden, autoHidden, shownAnyway).includes(column);
  const without = (list: string[]) => list.filter(c => c !== column);
  if (hidden) {
    return {
      userHidden: without(userHidden),
      shownAnyway: autoHidden.includes(column) ? [...without(shownAnyway), column] : shownAnyway
    };
  }
  return autoHidden.includes(column)
    ? { userHidden, shownAnyway: without(shownAnyway) }
    : { userHidden: [...userHidden, column], shownAnyway };
};
